import { useMarkets } from '@/state/markets';
import { useSettings } from '@/state/settings';
import { money } from '@/lib/format';
import { utcClock } from '@/lib/time';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { useSettingsPanel } from '@/components/SettingsPanel';
import { NextEventBadge } from '@/components/NextEventBadge';
import { useCinematic } from '@/components/CinematicTakeover';
import { tierForNetWorth } from '@/lib/tier';
import { useMeta } from '@/state/meta';
import { useLife } from '@/state/life';

export function HUDBar() {
  const cash = useMarkets((s) => s.cash);
  const positions = useMarkets((s) => s.positions);
  const realizedPnl = useMarkets((s) => s.realizedPnl);
  const equityATH = useMarkets((s) => s.equityATH);
  const muted = useSettings((s) => s.muted);
  const toggleMute = useSettings((s) => s.toggleMute);
  const playerHandle = useSettings((s) => s.playerHandle);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute net worth using current deterministic prices.
  let posValue = 0;
  for (const p of positions) {
    const cfg = getTicker(p.ticker);
    if (cfg) posValue += p.qty * priceAt(cfg, now);
  }
  const netWorth = cash + posValue;
  const todayPnl = realizedPnl;
  const pnlPositive = todayPnl >= 0;
  const tier = tierForNetWorth(netWorth);
  // record highest tier reached for meta-progression
  const recordTier = useMeta((s) => s.recordTier);
  const highestEver = useMeta((s) => s.highestTierRankReached);
  // Fire ASCENSION cinematic the FIRST time we cross into a new tier ever.
  // Compare against the snapshot we saw on mount so we don't replay celebrations
  // for tiers the player already reached on an earlier session.
  const baselineTier = useRef(highestEver);
  useEffect(() => {
    if (tier.rank > baselineTier.current) {
      useCinematic.getState().fire({
        id: `tier-up-${tier.rank}-${Date.now()}`,
        type: 'ascension',
        headline: `${tier.label.toUpperCase()}`,
        subhead: `net worth ${money(netWorth)} · welcome to a new tier`,
        durationMs: 4000,
      });
      baselineTier.current = tier.rank;
    }
    recordTier(tier.rank);
  }, [tier.rank, tier.label, recordTier, netWorth]);

  return (
    <header className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2 sm:px-4">
      <div className="flex items-center gap-3 sm:gap-6 min-w-0">
        <span className="text-mono-sm font-bold tracking-tight text-[var(--accent-primary)] shrink-0">
          nasdaq.one
        </span>
        <span className="hidden sm:inline text-mono-xs text-[var(--text-muted)] shrink-0">
          @{playerHandle}
        </span>
        <div className="hidden sm:flex items-center gap-4 text-mono-sm tabular-nums">
          <Stat label="NW" value={money(netWorth)} />
          <Stat
            label="P&L"
            value={money(todayPnl)}
            className={pnlPositive ? 'text-[var(--positive)]' : 'text-[var(--negative)]'}
          />
          <Stat label="CASH" value={money(cash)} />
          {equityATH > netWorth && equityATH > 10_000 && (
            <Stat
              label="DD"
              value={`${(((netWorth / equityATH) - 1) * 100).toFixed(1)}%`}
              className="text-[var(--negative)]"
            />
          )}
          <span className="text-mono-xs px-1.5 py-0.5 rounded border border-[var(--border-strong)] text-[var(--accent-primary)]" title={`Tier ${tier.rank}${equityATH > 0 ? ` · ATH ${money(equityATH)}` : ''}`}>
            {tier.label}
          </span>
        </div>
        <div className="sm:hidden flex items-center gap-2 text-mono-sm tabular-nums">
          <span>{money(netWorth)}</span>
          <span className={pnlPositive ? 'text-[var(--positive)]' : 'text-[var(--negative)]'}>
            {money(todayPnl)}
          </span>
          {equityATH > netWorth && equityATH > 10_000 && (
            <span className="text-[var(--negative)] text-mono-xs">
              {(((netWorth / equityATH) - 1) * 100).toFixed(0)}%
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <HeatBadges />
        <LawyerActiveBadge />
        <NextEventBadge />
        <span className="hidden sm:inline text-mono-xs text-[var(--text-muted)] tabular-nums">
          {utcClock(now)} UTC
        </span>
        <DashboardToggle />
        <SettingsToggle />
        <button
          onClick={toggleMute}
          className="rounded p-1.5 hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] transition-colors"
          aria-label={muted ? 'Unmute' : 'Mute'}
          title={muted ? 'Sound off' : 'Sound on'}
        >
          {muted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
        </button>
      </div>
    </header>
  );
}

function HeatBadges() {
  const sec = useLife((s) => s.secHeat);
  const irs = useLife((s) => s.irsHeat);
  const fbi = useLife((s) => s.fbiHeat);
  const badges: { label: string; value: number; threshold: number }[] = [
    { label: 'SEC', value: sec, threshold: 25 },
    { label: 'IRS', value: irs, threshold: 25 },
    { label: 'FBI', value: fbi, threshold: 25 },
  ];
  const visible = badges.filter((b) => b.value >= b.threshold);
  if (visible.length === 0) return null;
  return (
    <div className="hidden sm:flex items-center gap-1.5">
      {visible.map((b) => (
        <span
          key={b.label}
          className={cn(
            'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-mono-xs tabular-nums',
            b.value >= 75
              ? 'border-[var(--critical)] bg-[var(--critical)]/10 text-[var(--critical)] animate-pulse'
              : b.value >= 50
              ? 'border-[var(--negative)] text-[var(--negative)]'
              : 'border-[var(--warning)] text-[var(--warning)]',
          )}
          title={`${b.label} heat: ${b.value.toFixed(0)} / 100`}
        >
          {b.label} {b.value.toFixed(0)}
        </span>
      ))}
    </div>
  );
}

function LawyerActiveBadge() {
  const since = useLife((s) => s.lawyerRetainerActiveSince);
  if (since === null) return null;
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 rounded border border-[var(--negative)] bg-[var(--bg-elevated)] px-2 py-0.5 text-mono-xs text-[var(--negative)]"
      title="Lawyer is on retainer — $1,400/h draining your cash. Open the lawyer email to fire him."
    >
      ⚖ LAWYER ACTIVE · $1,400/h
    </span>
  );
}

function SettingsToggle() {
  const open = useSettingsPanel((s) => s.open);
  const toggle = useSettingsPanel((s) => s.toggle);
  return (
    <button
      onClick={toggle}
      className={cn(
        'rounded p-1.5 transition-colors',
        open
          ? 'bg-[var(--bg-elevated)] text-[var(--accent-primary)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
      )}
      aria-label="Settings"
      title="Settings"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </button>
  );
}

function DashboardToggle() {
  const open = useSettings((s) => s.showLifeDashboard);
  const set = useSettings((s) => s.setShowLifeDashboard);
  return (
    <button
      onClick={() => set(!open)}
      className={cn(
        'rounded p-1.5 transition-colors',
        open
          ? 'bg-[var(--bg-elevated)] text-[var(--accent-primary)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
      )}
      aria-label="Toggle stats dashboard"
      title="Stats"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 4 4 5-5" />
      </svg>
    </button>
  );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-mono-xs text-[var(--text-muted)]">{label}</span>
      <span className={cn('text-mono-sm tabular-nums', className)}>{value}</span>
    </div>
  );
}

function SpeakerOnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function SpeakerOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="22" y1="9" x2="16" y2="15" />
      <line x1="16" y1="9" x2="22" y2="15" />
    </svg>
  );
}
